/**
 * SwitchPsuSection — Compact inline manager for integrated PSU components
 * on a switch (or any rack_element).
 *
 * Renders a chip-based list of existing PSU components plus a small inline
 * form to add new ones.  Intended for use inside PortElementForm's power tab
 * so that each PSU can later be wired to a PDU outlet via PowerConnectionsPanel.
 */

import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

const EMPTY = { slot: '', power_watts: '', connector_type: '' };

/**
 * Optional API override props (for chassis or other entity types):
 *   fnGet(id, companyId)            → replaces api.getComponents
 *   fnCreate(id, data, companyId)   → replaces api.createComponent
 *   fnDelete(id, compId, companyId) → replaces api.deleteComponent
 */
function SwitchPsuSection({ elementId, companyId, fnGet, fnCreate, fnDelete }) {
  const _fnGet    = fnGet    || ((id, cid)            => api.getComponents(id, cid));
  const _fnCreate = fnCreate || ((id, data, cid)      => api.createComponent(id, data, cid));
  const _fnDelete = fnDelete || ((id, compId, cid)    => api.deleteComponent(id, compId, cid));

  const [psus,    setPsus]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding,  setAdding]  = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState(EMPTY);
  const [error,   setError]   = useState('');

  // ── load ────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!elementId) return;
    setLoading(true);
    try {
      const res = await _fnGet(elementId, companyId);
      setPsus((res.data || []).filter(c => c.component_type === 'psu'));
    } catch {
      setPsus([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementId, companyId, fnGet]);

  useEffect(() => { load(); }, [load]);

  // ── handlers ────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!form.slot.trim()) { setError('Introduce una etiqueta (ej. PSU-A)'); return; }
    setSaving(true);
    setError('');
    try {
      await _fnCreate(elementId, {
        component_type: 'psu',
        slot:           form.slot.trim(),
        power_watts:    form.power_watts ? Number(form.power_watts) : null,
        connector_type: form.connector_type || null,
        quantity:       1,
      }, companyId);
      setForm(EMPTY);
      setAdding(false);
      await load();
    } catch (e) {
      setError(e.message || 'Error al añadir fuente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (compId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('¿Eliminar esta fuente de alimentación? Si tiene una conexión PDU activa, se perderá.')) return;
    try {
      await _fnDelete(elementId, compId, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error al eliminar');
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ marginBottom: 28 }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h4 style={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151', margin: 0, letterSpacing: '0.03em' }}>
          ⚡ Fuentes de alimentación integradas
        </h4>
        {!adding && (
          <button
            className="btn btn-sm btn-outline-primary"
            type="button"
            onClick={() => { setAdding(true); setError(''); }}
            style={{ fontSize: '0.78rem', padding: '3px 10px' }}
          >
            + Añadir fuente
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <p className="text-muted" style={{ fontSize: '0.8rem', margin: 0 }}>Cargando…</p>
      )}

      {/* Empty state */}
      {!loading && psus.length === 0 && !adding && (
        <p className="text-muted" style={{ fontSize: '0.82rem', margin: 0 }}>
          Sin fuentes registradas. Añade al menos una para poder conectarla al PDU.
        </p>
      )}

      {/* PSU chips */}
      {psus.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: adding ? 12 : 0 }}>
          {psus.map(p => (
            <div
              key={p.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(16,185,129,.15)', border: '1px solid rgba(16,185,129,.4)',
                borderRadius: 8, padding: '5px 12px',
                fontSize: '0.82rem', color: '#166534',
              }}
            >
              <span style={{ fontWeight: 600 }}>⚡ {p.slot || `PSU #${p.id}`}</span>
              {p.power_watts ? (
                <span style={{ color: '#6b7280', fontSize: '0.77rem' }}>{p.power_watts} W</span>
              ) : null}
              {p.connector_type ? (
                <span style={{
                  background: 'rgba(14,165,233,.16)', color: '#7dd3fc',
                  borderRadius: 4, padding: '1px 6px', fontSize: '0.72rem',
                }}>
                  {p.connector_type}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => handleDelete(p.id)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#dc2626', fontSize: '0.78rem', padding: '0 2px',
                  lineHeight: 1, opacity: 0.7,
                }}
                title="Eliminar fuente"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {adding && (
        <div style={{
          background: 'var(--bg-toolbar)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '14px 16px', marginTop: 8,
        }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: '0.79rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>
                Etiqueta *
              </label>
              <input
                className="form-control form-control-sm"
                placeholder="PSU-A"
                value={form.slot}
                onChange={e => setForm(f => ({ ...f, slot: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                style={{ width: 110 }}
                autoFocus
              />
            </div>
            <div>
              <label style={{ fontSize: '0.79rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>
                Potencia (W)
              </label>
              <input
                className="form-control form-control-sm"
                type="number"
                placeholder="480"
                value={form.power_watts}
                onChange={e => setForm(f => ({ ...f, power_watts: e.target.value }))}
                style={{ width: 90 }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.79rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>
                Conector
              </label>
              <select
                className="form-select form-select-sm"
                value={form.connector_type}
                onChange={e => setForm(f => ({ ...f, connector_type: e.target.value }))}
                style={{ width: 130 }}
              >
                <option value="">— tipo —</option>
                <option value="iec320-c13">IEC C13 (10 A)</option>
                <option value="iec320-c19">IEC C19 (16 A)</option>
                <option value="schuko">Schuko</option>
                <option value="nema-5-15">NEMA 5-15</option>
                <option value="nema-5-20">NEMA 5-20</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 6, paddingBottom: 1 }}>
              <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={handleAdd}
                disabled={saving}
                style={{ fontSize: '0.8rem' }}
              >
                {saving ? '…' : 'Añadir'}
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                type="button"
                onClick={() => { setAdding(false); setForm(EMPTY); setError(''); }}
                style={{ fontSize: '0.8rem' }}
              >
                Cancelar
              </button>
            </div>
          </div>
          {error && (
            <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: 8 }}>{error}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default SwitchPsuSection;
